import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { Button } from "./button";
import { Icon, type IconName } from "./icons";

// ConfirmDialog — codified from 14-diyaloglar.html. A soft overlay, a centered card
// with one icon, a title, a body, and exactly two actions (cancel + one clear primary).
// Presentational + controlled: `open`, `onCancel`, `onConfirm` are the caller's; the
// destructive tone uses the solid-danger button. Focuses the confirm on open, closes on
// Escape and overlay click.
export type DialogTone = "danger" | "primary";

export interface ConfirmDialogProps {
  open: boolean;
  tone?: DialogTone;
  icon?: IconName;
  title: ReactNode;
  description?: ReactNode;
  cancelLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  confirming?: boolean;
  /** aria-label for the dialog when the title is not a plain string. */
  ariaLabel?: string;
}

export function ConfirmDialog({
  open,
  tone = "danger",
  icon,
  title,
  description,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  confirming = false,
  ariaLabel,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const glyph: IconName = icon ?? (tone === "danger" ? "trash" : "info");

  return (
    <div className="k-overlay" onClick={onCancel}>
      <div
        className="k-dialog"
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : ariaLabel}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`k-dialog__icon k-dialog__icon--${tone}`}>
          <Icon name={glyph} size={23} strokeWidth={1.9} />
        </div>
        <div className="k-dialog__title">{title}</div>
        {description ? <div className="k-dialog__desc">{description}</div> : null}
        <div className="k-dialog__actions">
          <Button variant="secondary" type="button" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "danger" ? "danger-solid" : "primary"}
            type="button"
            loading={confirming}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
